import _ from 'lodash'
import * as React from 'react'
import Fuse from 'fuse.js'
import classNames from 'classnames'
import i18next from 'i18next'
import * as sentry from '../../src/utils/sentry'
import { Page } from '../../../app-core/lib/page/Page'
import { TopBar } from '../../../app-core/lib/page/TopBar'
import { TopBarTitle } from '../../../app-core/lib/page/TopBarTitle'
import { Base_commonDeps } from '../Base/Base.commonDeps'
import type { Base_IContext } from '../Base/Base.IContext'
import { Base_PureComponent } from '../Base/Base.ui.Component'
import type { Proc_Procedure } from '../Proc/Proc.Procedure'
import { WF_filterOpenWorkflow } from './WF.filterOpenWorkflow'
import type { WF_OpenWorkflow } from './WF.OpenWorkflow'
import { WF_ProcedureSearcher } from './WF.ProcedureSearcher'
import type { WF_RecentlyUsedWorkflow } from './WF.RecentlyUsedWorkflow'
import { WF_ChooseWorkflowSection } from './WF.ui.ChooseWorkflowSection'
import { WorkflowItem } from '../../../app-core/lib/ui/WorkflowItem'
import { LabelledIconButton } from '../../../app-core/lib/ui/LabelledIconButton'
import { SearchBox } from '../../../app-core/lib/ui/SearchBox'
import { TopBarCloseButton } from '../../../app-core/lib/page/TopBarButtons'
import { Pulse } from '../../../app-core/lib/ui/Pulse'
import { Translations } from '../../../shared/i18n/Translations'
import type { Proc_ProcedureWorkflow } from '../Proc/Proc.ProcedureWorkflow'
import { isWorkflowForGuide, type AppTrainingWorkflow } from '../../../app-core/lib/workflow/Workflow'
import { Placeholder } from '../../../app-core/lib/ui/Placeholder'
import { Rout_toNewNote, Rout_toNotebook } from '../Rout/Rout.note'
import * as LaunchDarkly from '../Util/Util.launchdarkly'
import type { AutoOpen } from '../Note/Note.AutoOpenPlugin'

type TProps = {
    openWorkflow?: WF_OpenWorkflow
    recentlyUsedWorkflows: WF_RecentlyUsedWorkflow[]
    missingTrainingRecommendationsMap: Map<number, AppTrainingWorkflow[]>
    procedures: Proc_Procedure[]
    onClose: (e?: React.MouseEvent<any>) => void
    onWorkflowSelect: (workflowId: number, procedureId: number) => Promise<void>
    onInProgressSelect: () => void
}

type Selected =
    | { type: 'inProgress' }
    | { value: { workflowId: number; procedureId: number }; type: 'recentlyUsed' | 'procedureWorkflow' }

type TState = {
    isLoading: boolean
    selected?: Selected
    searchTerm: string
    filteredProcedures: Proc_Procedure[]
    filteredRecentlyUsedWorkflows: WF_RecentlyUsedWorkflow[]
    filteredOpenWorkflow: WF_OpenWorkflow
    isNotesFeatureEnabled: boolean
}

export class WF_ChooseWorkflow extends Base_PureComponent<TProps, TState> {
    mounted = false
    public state = {
        isLoading: false,
        selected: null as Selected,
        searchTerm: '',
        filteredProcedures: this.props.procedures,
        filteredRecentlyUsedWorkflows: this.props.recentlyUsedWorkflows,
        filteredOpenWorkflow: this.props.openWorkflow,
        isNotesFeatureEnabled: false,
    }

    private procedureSearcher: WF_ProcedureSearcher
    private bodyRef: HTMLDivElement

    constructor(props: TProps, context: Base_IContext) {
        super(props, context)
        this.procedureSearcher = new WF_ProcedureSearcher(props.procedures)
    }

    private selectProcedureWorkflow = (
        workflowId: number,
        procedureId: number,
        type: 'recentlyUsed' | 'procedureWorkflow' = 'procedureWorkflow'
    ) => {
        const newSelected: Selected = {
            value: {
                workflowId,
                procedureId,
            },
            type,
        }
        this.select(newSelected)
    }

    private selectInProgress() {
        this.select({ type: 'inProgress' })
    }

    async componentDidMount() {
        const isNotesFeatureEnabled = LaunchDarkly.getFlagValue('notes', false)

        this.setState({ isNotesFeatureEnabled })

        this.mounted = true
    }

    componentWillUnmount() {
        this.mounted = false
    }

    componentDidUpdate(): void {
        const { isLoading, selected } = this.state

        if (isLoading || !selected) {
            return
        }

        this.onSubmit()
    }

    private select = (newSelected: Selected) => {
        const { selected } = this.state
        /* eslint-disable-next-line @typescript-eslint/no-unused-expressions */
        _.isEqual(selected, newSelected)
            ? this.setState({ selected: null }) // deselect if the current selection is clicked again
            : this.setState({ selected: newSelected })
    }

    private onClose = (e: React.MouseEvent<any>) => {
        e.preventDefault()
        this.props.onClose(e)
    }

    private onSubmit = async () => {
        const { selected } = this.state

        if (selected?.type === 'inProgress') {
            this.props.onInProgressSelect()
        } else {
            try {
                this.setState({ isLoading: true })
                await this.props.onWorkflowSelect(selected.value.workflowId, selected.value.procedureId)
                if (this.mounted) {
                    this.setState({ isLoading: false })
                }
            } catch (error) {
                sentry.err(error, { extra: { ...selected.value } })
                if (this.mounted) {
                    this.setState({ isLoading: false })
                }
            }
        }
    }

    private onClickNewNoteButton = (autoOpen: AutoOpen) => {
        Rout_toNewNote(autoOpen)
        this.props.onClose()
    }

    private onClickNotebookButton = () => {
        Rout_toNotebook()
        this.props.onClose()
    }

    private handleSearchInputChange = (searchTerm: string) => {
        this.setState({
            filteredProcedures: this.procedureSearcher.search(searchTerm),
            filteredRecentlyUsedWorkflows: searchTerm
                ? new Fuse(this.props.recentlyUsedWorkflows, {
                      keys: ['procedureName', 'workflowName'],
                      threshold: 0.3,
                  })
                      .search(searchTerm)
                      .map((result) => result.item)
                : this.props.recentlyUsedWorkflows,
            filteredOpenWorkflow: WF_filterOpenWorkflow(this.props.openWorkflow, searchTerm),
            searchTerm,
        })
        this.bodyRef?.scrollTo(0, 0)
    }

    private isProcessWorkflowSelected(
        workflowId: number,
        type: 'recentlyUsed' | 'procedureWorkflow' = 'procedureWorkflow'
    ): boolean {
        const { selected } = this.state
        if (!selected) return false
        return selected.type === type && selected.value.workflowId === workflowId
    }

    private renderRecentlyUsedWorkflows() {
        if (this.state.filteredRecentlyUsedWorkflows.length === 0) return null

        return (
            <div className="WF_ChooseWorkflow_recentlyUsed" data-testid="WF_ChooseWorkflow_recentlyUsed">
                <WF_ChooseWorkflowSection heading={Translations.t('app:WF.ChooseWorkflow.recentlyUsed')}>
                    {this.state.filteredRecentlyUsedWorkflows.map(
                        ({ workflowId, workflowName, workflowType, procedureId, procedureName }) => {
                            const hasMissingTrainingRecommendations =
                                this.props.missingTrainingRecommendationsMap.get(workflowId)?.length > 0

                            return (
                                <WorkflowItem
                                    key={workflowId}
                                    onClick={() =>
                                        this.selectProcedureWorkflow(workflowId, procedureId, 'recentlyUsed')
                                    }
                                    selected={this.isProcessWorkflowSelected(workflowId, 'recentlyUsed')}
                                    procedureName={procedureName}
                                    workflowName={workflowName}
                                    workflowType={workflowType}
                                    hasMissingTrainingRecommendations={hasMissingTrainingRecommendations}
                                />
                            )
                        }
                    )}
                </WF_ChooseWorkflowSection>
            </div>
        )
    }

    private renderWorkflow = (workflow: Proc_ProcedureWorkflow, procedure: Proc_Procedure) => {
        // we don't want to display Guide workflows in the ad-hoc menu
        if (isWorkflowForGuide(workflow)) return null

        const hasMissingTrainingRecommendations =
            this.props.missingTrainingRecommendationsMap.get(workflow.id)?.length > 0

        return (
            <WorkflowItem
                key={workflow.id}
                onClick={() => this.selectProcedureWorkflow(workflow.id, procedure.id)}
                selected={this.isProcessWorkflowSelected(workflow.id)}
                workflowName={workflow.name}
                workflowType={workflow.type}
                hasMissingTrainingRecommendations={hasMissingTrainingRecommendations}
            />
        )
    }

    private renderProcedures = () => {
        return (
            <div className="WF_ChooseWorkflow_workflows">
                {this.state.filteredProcedures.map((procedure) => (
                    <WF_ChooseWorkflowSection key={procedure.id} heading={procedure.name}>
                        {procedure.workflows.map((workflow) => this.renderWorkflow(workflow, procedure))}
                    </WF_ChooseWorkflowSection>
                ))}
            </div>
        )
    }

    private renderInProgressWorkflow() {
        const { filteredOpenWorkflow, selected } = this.state

        if (!filteredOpenWorkflow) return null

        return (
            <div className="WF_ChooseWorkflow_inProgress">
                <WF_ChooseWorkflowSection heading={Translations.t('app:Todo.ToDoPage.youAreCurrently')}>
                    <WorkflowItem
                        onClick={() => this.selectInProgress()}
                        procedureName={filteredOpenWorkflow.procedureName}
                        selected={selected && selected.type === 'inProgress'}
                        workflowName={filteredOpenWorkflow.workflowName}
                        workflowType={filteredOpenWorkflow.workflowType}
                    />
                </WF_ChooseWorkflowSection>
            </div>
        )
    }

    private renderNoWorkflowsMessage() {
        if (
            this.state.filteredOpenWorkflow !== null ||
            this.state.filteredProcedures.length !== 0 ||
            this.state.filteredRecentlyUsedWorkflows.length !== 0
        ) {
            return null
        }

        return (
            <Placeholder
                icon="empty-results"
                title={Translations.t('app:UI.common.noTasks')}
                message={Translations.t('app:UI.common.foundNoResults')}
            />
        )
    }

    private renderHeader = () => {
        return (
            <div className={classNames('WF_ChooseWorkflow_header', { dark: this.state.isNotesFeatureEnabled })}>
                <TopBar
                    deps={Base_commonDeps()}
                    leftButton={() => (
                        <TopBarCloseButton
                            onClick={this.onClose}
                            dataTestId="close-choose-workflow"
                            dark={this.state.isNotesFeatureEnabled}
                        />
                    )}
                    rightButton={() =>
                        this.state.isNotesFeatureEnabled && (
                            <zaptic-icon
                                class="WF_ChooseWorkflow_notebook-icon"
                                icon-name="book"
                                onClick={this.onClickNotebookButton}
                            />
                        )
                    }
                    bordered={!this.state.isNotesFeatureEnabled}
                    dark={this.state.isNotesFeatureEnabled}
                >
                    {() => (
                        <TopBarTitle
                            deps={Base_commonDeps()}
                            header={Translations.t('app:Todo.ScheduledReportSummary.startTask').toUpperCase()}
                            dark={this.state.isNotesFeatureEnabled}
                        />
                    )}
                </TopBar>
                {this.state.isNotesFeatureEnabled && (
                    <div className="WF_ChooseWorkflow_note-section">
                        <LabelledIconButton
                            label={i18next.t('app:Note.StartPage.writeButtonLabel')}
                            iconName={'edit'}
                            onClick={() => this.onClickNewNoteButton('none')}
                        />
                        <LabelledIconButton
                            label={i18next.t('app:Note.StartPage.captureButtonLabel')}
                            iconName={'camera'}
                            onClick={() => this.onClickNewNoteButton('photo')}
                        />
                        <LabelledIconButton
                            label={i18next.t('app:Note.StartPage.recordButtonLabel')}
                            iconName={'video-camera'}
                            onClick={() => this.onClickNewNoteButton('video')}
                        />
                    </div>
                )}
                <div className="WF_ChooseWorkflow_search-section">
                    <SearchBox
                        deps={Base_commonDeps()}
                        data-testid="start-task-search-input"
                        size="large"
                        placeholder={Translations.t('app:common.search')}
                        searchTerm={this.state.searchTerm}
                        onChange={this.handleSearchInputChange}
                    />
                </div>
            </div>
        )
    }

    private renderLoading() {
        const { isLoading } = this.state

        return isLoading ? <Pulse /> : ''
    }

    public tryRender() {
        return (
            <div className="WF_ChooseWorkflow_root">
                <Page deps={Base_commonDeps()} header={this.renderHeader()}>
                    {this.renderInProgressWorkflow()}
                    {this.renderRecentlyUsedWorkflows()}
                    {this.renderProcedures()}
                    {this.renderNoWorkflowsMessage()}
                    {this.renderLoading()}
                </Page>
            </div>
        )
    }
}
